import React, { useState } from 'react';
import cn from 'classnames';

import { ITabForm } from '@interfaces/components/form';

import styles from './FormWithTabs.module.scss';

interface IFormWithTabsProps {
	tabs: Array<ITabForm>;
	initialState?: Record<string, any>;
	saveValues?: (values: Record<string, any>) => void;
	isEditMode?: boolean;
	isViewMode?: boolean;
	setIsEmptyErrors?: React.Dispatch<React.SetStateAction<boolean>>;
	isEmptyErrors?: boolean;
	stickyTabs?: boolean;
}

const FormWithTabs = ({
	tabs,
	initialState,
	saveValues,
	isEditMode,
	isViewMode,
	setIsEmptyErrors,
	isEmptyErrors = true,
	stickyTabs = false,
}: IFormWithTabsProps) => {
	const [currentTab, setCurrentTab] = useState(tabs[0].value);

	const generateComponent = (index: number) => {
		const Component = tabs[index].component;
		const props = tabs[index].props;

		return (
			<Component
				initialState={initialState}
				isEditMode={isEditMode}
				isViewMode={isViewMode}
				saveValues={saveValues}
				setCurrentTab={setCurrentTab}
				setIsEmptyErrors={setIsEmptyErrors}
				{...props}
			/>
		);
	};

	const getCurrentTab = () => {
		const currentIndex = tabs.findIndex(tab => tab.value === currentTab);

		return generateComponent(currentIndex >= 0 ? currentIndex : 0);
	};

	return (
		<div className={styles.formWithTabsContainer}>
			<div className={cn(styles.tabsContainer, { [styles.sticky]: stickyTabs })}>
				{tabs.filter((item) => !item.isHidden).map((tab, i) => {
					const changeTab = () => {
						if ((isEditMode || isViewMode) && isEmptyErrors) setCurrentTab(tab.value);

						if (setIsEmptyErrors) setIsEmptyErrors(false);
					};
					const selectedIndex = tabs.findIndex((item) => currentTab === item.value);
					const isViewed = i < selectedIndex;
					const tabClasses = cn(styles.tab, {
						[styles.active]: currentTab == tab.value || (isViewed && !isEditMode && !isViewMode),
						[styles.underline]: currentTab == tab.value,
						[styles.clickable]: isEditMode || isViewMode,
					});
					const indexClasses = cn(styles.index, {
						[styles.boxShadow]: currentTab == tab.value && !isViewed,
					});
					const titleClasses = cn(styles.title, {
						[styles.error]: tab.isTabError,
					});

					return (
						<div
							key={`${tab.value}-${i}`}
							className={tabClasses}
							onClick={changeTab}
						>
							{!isEditMode && <div className={indexClasses}>{i + 1}</div>}
							<div className={titleClasses}>{tab.title}</div>
						</div>
					);
				})}
			</div>
			<div className={styles.formContent}>
				{getCurrentTab()}
			</div>
		</div>
	);
};

export default FormWithTabs;
